import React, { useState, useEffect } from 'react';
import { Modal, Typography, Space, Button, Row, Col, InputNumber, Select, Divider, Tag, App, Tabs, Checkbox } from 'antd';
import {
  CreditCardOutlined,
  DollarOutlined,
  BankOutlined,
  MobileOutlined,
  GiftOutlined,
  CheckCircleOutlined,
  PrinterOutlined,
  MinusOutlined,
  PlusOutlined
} from '@ant-design/icons';
import { Table } from '../../../types/tables';
import { Order } from '../../../types/orders';
import { useResponsive } from '../../../hooks/useResponsive';
import { useGetOrderDiscountsQuery } from '../../../services/ordersApi';
import { roundCents, withDiscountLabels } from '../../../utils/discounts';
import { newClientId } from '../../../utils/clientId';
import { useGetPaymentMethodsQuery } from '../../../services/paymentMethodsApi';
import { useGetRestaurantSettingsQuery } from '../../../services/restaurantsApi';
import { useGetBoolSettingQuery, useGetSettingQuery } from '../../../services/settingsApi';
import { useGetFacturacionConfigQuery } from '../../../services/facturacionApi';
import ReceiptPreviewModal from '../../printers/ReceiptPreviewModal';
import { printAgentService } from '../../../services/printAgentService';
import { buildOrderTicketPreview, withPrintAnimation } from '../../../services/printAnimation';
import { formatOrderNumber } from '../../../utils/orderNumber';
import { shouldUseCloudPrint } from '../../../utils/cloudPrintRoute';
import { bluetoothPrintService } from '../../../services/bluetoothPrintService';
import { useCloudPrint } from '../../../hooks/useCloudPrint';
import TerminalPointSection, { TerminalPaymentTarget } from './TerminalPointSection';
import { TerminalPaymentIntent } from '../../../services/mercadoPagoApi';

const { Title, Text } = Typography;
const { Option } = Select;

export interface PaymentMethod {
  id: string;
  name: string;
  icon: React.ReactNode;
  color: string;
  requiresReference?: boolean;
  maxAmount?: number;
}

export interface PaymentEntry {
  id: string;
  methodId: string;
  amount: number;
  reference?: string;
}

interface PaymentModalProps {
  visible: boolean;
  onCancel: () => void;
  table?: Table;
  order?: Order;
  discountAmount?: number;
  discountPercentage?: number;
  /**
   * Número de cuenta cuando se cobra un split de la orden. Viaja en el QR de
   * autofactura del ticket para que el cliente facture solo su cuenta.
   */
  autofacturaCuenta?: number;
  onProcessPayment?: (payments: PaymentEntry[], tip: number) => Promise<void>;
  onPrintReceipt?: (payments: PaymentEntry[], tip: number) => void;
  /**
   * Qué cobra la terminal Mercado Pago Point (orden completa o splitId).
   * Explícito a propósito: en splits la orden sintética conserva el order.id
   * real y derivarlo aquí cobraría la orden completa. Sin este prop (junto con
   * onTerminalPaymentSettled) la opción de terminal no se muestra.
   */
  terminalTarget?: TerminalPaymentTarget;
  /**
   * El webhook de MP ya registró el pago y cerró la cuenta en el backend; el
   * padre solo hace su post-pago (mesa a Dirty, eventos, refetch) SIN llamar a
   * processPayment.
   */
  onTerminalPaymentSettled?: () => Promise<void> | void;
  /**
   * La orden ya tiene la cuenta dividida y el backend rechazó el cobro
   * completo. El padre debe llevar a quien cobra al cobro por cuenta. Aplica
   * tanto al cobro manual como al de terminal: los dos caminos terminan en el
   * mismo rechazo.
   */
  onOrderAlreadySplit?: () => void;
}

/** Métodos de tarjeta (códigos nuevos y legado). */
const ES_TARJETA = ['card', 'credit_card', 'debit_card', 'card_credit', 'card_debit'];

/** Métodos que muestran el campo "Referencia" aunque no la exijan. */
const METODOS_CON_REFERENCIA = [...ES_TARJETA, 'transfer', 'mobile'];

const PaymentModal: React.FC<PaymentModalProps> = ({
  visible,
  onCancel,
  table,
  order,
  discountAmount = 0,
  discountPercentage = 0,
  autofacturaCuenta,
  onProcessPayment,
  onPrintReceipt,
  terminalTarget,
  onTerminalPaymentSettled,
  onOrderAlreadySplit,
}) => {
  const { message } = App.useApp();
  const { isMobile } = useResponsive();
  const [payments, setPayments] = useState<PaymentEntry[]>([]);
  const [selectedMethodId, setSelectedMethodId] = useState<string>('');
  const [currentAmount, setCurrentAmount] = useState<number>(0);
  const [currentReference, setCurrentReference] = useState<string>('');
  const [suggestedTip, setSuggestedTip] = useState<number>(0);
  const [customTip, setCustomTip] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [printAgentAvailable, setPrintAgentAvailable] = useState(false);
  const [bluetoothAvailable, setBluetoothAvailable] = useState(false);
  const [printTicket, setPrintTicket] = useState(true);

  // Cloud print
  const { data: cloudPrintSetting } = useGetBoolSettingQuery('CloudPrintEnabled');
  // Nube solo con servidor y con la orden ya en él: ver shouldUseCloudPrint.
  const cloudPrintSettingOn = cloudPrintSetting?.value ?? false;
  const { printJob: cloudPrintJob } = useCloudPrint();

  // Get print ticket default setting
  const { data: printTicketSetting } = useGetBoolSettingQuery('ImprimirTicket_LiquidarCuenta');

  // Terminal bancaria: TerminalProvider dice qué proveedor está activo
  // (exclusividad aplicada por el backend). MP requiere además el feature flag
  // del módulo (solo soporte, en piloto); Getnet nace abierto. Y siempre: un
  // padre que sepa cerrar el flujo post-pago (el webhook registra el pago solo).
  const { data: terminalProviderSetting } = useGetSettingQuery('TerminalProvider');
  const { data: mpPointModuleSetting } = useGetBoolSettingQuery('MercadoPagoPointActivo');
  const { data: mpPointSetting } = useGetBoolSettingQuery('MercadoPagoPointEnabled');
  const { data: getnetPointSetting } = useGetBoolSettingQuery('GetnetPointEnabled');
  const terminalProvider = (terminalProviderSetting?.value ?? '') as '' | 'mercadopago' | 'getnet';
  const providerReady = terminalProvider === 'mercadopago'
    ? (mpPointModuleSetting?.value ?? false) && (mpPointSetting?.value ?? false)
    : terminalProvider === 'getnet'
      ? (getnetPointSetting?.value ?? false)
      : false;
  const terminalPayEnabled = providerReady && !!terminalTarget && !!onTerminalPaymentSettled;
  const [terminalSettling, setTerminalSettling] = useState(false);

  // Set default value from setting when loaded
  useEffect(() => {
    if (printTicketSetting !== undefined) {
      setPrintTicket(printTicketSetting.value ?? true);
    }
  }, [printTicketSetting]);

  // Get restaurant settings for tax rate
  const { data: restaurantSettings } = useGetRestaurantSettingsQuery();
  // Get facturacion config for autofactura URL on tickets
  const { data: facturacionConfig } = useGetFacturacionConfigQuery();
  const taxRate = (restaurantSettings?.taxRate || 16) / 100; // Convert percentage to decimal

  // Get discounts for this order. Al cobrar una cuenta dividida la orden
  // sintética conserva el order.id real: restar aquí los descuentos de la
  // orden completa sobre los items de UNA cuenta la subcobraría — los
  // descuentos de orden están congelados/bloqueados en órdenes divididas.
  const {
    data: orderDiscounts
  } = useGetOrderDiscountsQuery(order?.id || '', {
    skip: !order?.id || !!autofacturaCuenta
  });

  // Get payment methods from API
  const {
    data: apiPaymentMethods
  } = useGetPaymentMethodsQuery({ activeOnly: true });

  // Al abrir: se limpia lo del cobro anterior y se deja marcado el método por
  // defecto (el primero de la lista, normalmente Efectivo) EN LA MISMA PASADA.
  //
  // Iban en dos efectos separados y se pisaban: el de reset corría después y
  // dejaba el método en vacío. Con el modal ya montado de antes (cobro desde la
  // mesa) no se notaba, porque al limpiarlo cambiaba la dependencia del otro
  // efecto y este volvía a marcar el método. Pero el cobro de una cuenta
  // dividida monta el modal ya visible: los dos efectos corrían una sola vez,
  // en ese orden, y la caja se quedaba sin método marcado.
  React.useEffect(() => {
    if (!visible) return;
    setPayments([]);
    setCurrentAmount(0);
    setCurrentReference('');
    setSuggestedTip(0);
    setCustomTip(0);
    setSelectedMethodId(apiPaymentMethods?.[0]?.id ?? '');
    // Solo al abrir: si los métodos llegan después, los marca el efecto de abajo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // Respaldo para cuando los métodos de pago todavía no habían cargado al abrir.
  React.useEffect(() => {
    if (visible && !selectedMethodId && apiPaymentMethods && apiPaymentMethods.length > 0) {
      setSelectedMethodId(apiPaymentMethods[0].id);
    }
  }, [visible, apiPaymentMethods, selectedMethodId]);

  // Check for available print services when modal opens
  React.useEffect(() => {
    const checkPrintServices = async () => {
      // Check Web Bluetooth API (Android/Chrome)
      const btAvailable = bluetoothPrintService.isAvailable();
      setBluetoothAvailable(btAvailable);

      setPrintAgentAvailable(await printAgentService.isAvailable());
    };
    if (visible) {
      checkPrintServices();
    }
  }, [visible]);

  if (!order) return null;

  // El prop `table` solo llega desde el menú de mesas. Cobrando desde la lista de
  // órdenes no hay objeto Table a mano, así que la mesa se identifica con
  // order.tableName (el número de mesa, ver MappingProfile); TakeOut/Domicilio no
  // tienen mesa real.
  const orderTableLabel = order.type === 'DineIn'
    ? (order.tableName ? `Mesa ${order.tableName}` : 'Mesa')
    : order.type === 'Delivery' ? 'Domicilio' : 'Para Llevar';
  const displayLabel = table ? `Mesa ${table.number}` : orderTableLabel;
  const displayAreaName = table?.areaName || '';
  const tableNumber = table?.number?.toString()
    || (order.type === 'DineIn' ? (order.tableName || '') : orderTableLabel);

  // Map API payment methods to UI format with icons
  const getPaymentMethodIcon = (code: string) => {
    const iconMap: Record<string, React.ReactNode> = {
      'CASH': <DollarOutlined />,
      'CARD': <CreditCardOutlined />,
      'CREDIT_CARD': <CreditCardOutlined />,
      'DEBIT_CARD': <CreditCardOutlined />,
      'TRANSFER': <BankOutlined />,
      'MOBILE': <MobileOutlined />,
      'VOUCHER': <GiftOutlined />,
      'CHECK': <BankOutlined />,
    };
    return iconMap[code.toUpperCase()] || <DollarOutlined />;
  };

  const getPaymentMethodColor = (code: string) => {
    const colorMap: Record<string, string> = {
      'CASH': '#52c41a',
      'CARD': '#1890ff',
      'CREDIT_CARD': '#1890ff',
      'DEBIT_CARD': '#13c2c2',
      'TRANSFER': '#722ed1',
      'MOBILE': '#fa8c16',
      'VOUCHER': '#eb2f96',
      'CHECK': '#13c2c2',
    };
    return colorMap[code.toUpperCase()] || '#1890ff';
  };

  const paymentMethods: PaymentMethod[] = (apiPaymentMethods || []).map(pm => ({
    id: pm.id,
    name: pm.name,
    icon: getPaymentMethodIcon(pm.code),
    color: getPaymentMethodColor(pm.code),
    requiresReference: pm.requiresReference,
  }));

  // Calculate totals
  const orderItems = order.orderItems || [];
  const activeItems = orderItems.filter(item => !item.isCancelled);

  // NOTE: Total price is used as base for tax calculation
  // Tax is calculated as a percentage of the total, then subtracted to get subtotal
  const totalWithTax = activeItems.reduce((sum, item) => sum + item.total, 0);

  // Extract tax from total (prices already include IVA)
  // Subtotal = Total / (1 + taxRate), Tax = Total - Subtotal
  const subtotal = totalWithTax / (1 + taxRate);
  const tax = totalWithTax - subtotal;
  const totalBeforeDiscount = totalWithTax;
  
  // Calculate discount amount from applied discounts (from API)
  // Los de producto salen como "Cortesía (Café)" en pantalla y en el ticket
  const appliedDiscounts = withDiscountLabels(orderDiscounts || [], order?.orderItems);
  const totalDiscountAmount = appliedDiscounts.reduce((sum, discount) => sum + discount.discountAmount, 0);
  
  const totalAfterDiscount = Math.max(roundCents(totalBeforeDiscount - totalDiscountAmount), 0);
  const finalTip = suggestedTip > 0 ? suggestedTip : customTip;
  const grandTotal = totalAfterDiscount + finalTip;

  // Calculate amounts
  const totalPaid = payments.reduce((sum, payment) => sum + payment.amount, 0);
  const remainingAmount = grandTotal - totalPaid;
  const changeAmount = totalPaid > grandTotal ? totalPaid - grandTotal : 0;

  const currentMethod = paymentMethods.find(m => m.id === selectedMethodId);

  // Suggested tip percentages
  const tipSuggestions = [
    { label: '10%', value: totalAfterDiscount * 0.10 },
    { label: '15%', value: totalAfterDiscount * 0.15 },
    { label: '20%', value: totalAfterDiscount * 0.20 },
  ];

  const handleAddPayment = () => {
    if (currentAmount <= 0) {
      message.error('El monto debe ser mayor a cero');
      return;
    }

    if (currentMethod?.requiresReference && !currentReference.trim()) {
      message.error('Este método de pago requiere una referencia');
      return;
    }

    // Find the payment method to get its code
    const selectedPaymentMethod = apiPaymentMethods?.find(pm => pm.id === selectedMethodId);
    if (!selectedPaymentMethod) {
      message.error('Método de pago no encontrado');
      return;
    }

    const newPayment: PaymentEntry = {
      // UUID y no Date.now(): viaja al servidor como Id del pago. Mientras el renglón
      // siga en la lista, pulsar "Cobrar" otra vez tras un timeout es el MISMO cobro
      // y el servidor no lo registra dos veces.
      id: newClientId(),
      methodId: selectedPaymentMethod.code, // Use code instead of ID
      amount: currentAmount,
      reference: currentReference.trim() || undefined,
    };

    setPayments([...payments, newPayment]);
    setCurrentAmount(0);
    setCurrentReference('');
    message.success('Pago agregado');
  };

  const handleRemovePayment = (paymentId: string) => {
    setPayments(payments.filter(p => p.id !== paymentId));
  };

  const handleQuickAmount = (amount: number) => {
    setCurrentAmount(amount);
  };

  const handleSuggestedTip = (amount: number) => {
    // If clicking the same tip amount, deselect it
    if (suggestedTip === amount) {
      setSuggestedTip(0);
    } else {
      setSuggestedTip(amount);
    }
    setCustomTip(0);
  };

  const handleProcessPayment = async () => {
    if (totalPaid < grandTotal - 0.01) {
      message.error('El pago no está completo');
      return;
    }

    if (payments.length === 0) {
      message.error('Debe agregar al menos un método de pago');
      return;
    }

    if (!onProcessPayment) {
      message.error('Error: No se puede procesar el pago - callback no definido');
      return;
    }

    setLoading(true);
    try {
      // First, process the payment (this is critical)
      await onProcessPayment(payments, finalTip);

      // Then try to print only if printTicket is enabled
      if (printTicket) {
        await printPaymentTicketSafe(payments, finalTip);
      } else {
        // Print ticket disabled
        message.success('Pago procesado exitosamente');
      }

      onCancel();
    } catch (error: any) {
      // Error is already handled and displayed by TablesManager
      // Just log it here and re-throw
      console.error('Payment error:', error);
      // Don't re-throw - TablesManager already handles the error
    } finally {
      setLoading(false);
    }
  };

  const isPaymentComplete = totalPaid >= grandTotal - 0.01;

  // Impresión del ticket de pago, compartida entre el cobro manual y el cobro
  // en terminal (que trae su propio desglose de pagos y propina). Nunca lanza:
  // el pago ya quedó registrado y una falla de impresión no debe deshacerlo.
  async function printPaymentTicketSafe(paymentsToPrint: PaymentEntry[], tipToPrint: number) {
    const totalToPrint = totalAfterDiscount + tipToPrint;

    // Build autofactura QR URL if enabled. The total must be what the backend
    // validates against: the charged amount WITHOUT tip (order.Total for whole
    // orders, the cuenta's charged amount for splits) — the grand total includes
    // the tip and would never match. &cuenta identifies the split being invoiced.
    // Una orden cobrada sin conexión todavía no tiene su número (es 0 hasta sincronizar) y
    // no se factura nada sin sincronizar: su QR mandaría al cliente a facturar la orden 0.
    const autofacturaUrl = facturacionConfig?.autofacturaEnabled && facturacionConfig?.autofacturaCode
      && !order!.offlineFolioLabel
      ? `${window.location.origin}/autofactura/${facturacionConfig.autofacturaCode}?order=${order!.orderNumber}&total=${totalAfterDiscount.toFixed(2)}${autofacturaCuenta ? `&cuenta=${autofacturaCuenta}` : ''}`
      : undefined;

    // Map payment codes to display names for printing
    const printPayments = paymentsToPrint.map(p => {
      const method = apiPaymentMethods?.find(pm => pm.code === p.methodId);
      return { ...p, methodId: method?.name || p.methodId };
    });

    const preview = buildOrderTicketPreview({
      kind: 'cuenta',
      title: 'TICKET DE PAGO',
      tableLabel: tableNumber,
      waiterName: order!.assignedWaiterName || undefined,
      orderLabel: formatOrderNumber(order!),
      items: order!.orderItems,
      subtotal,
      tax,
      discounts: appliedDiscounts,
      tip: tipToPrint,
      total: totalToPrint,
      payments: printPayments.map(p => ({ method: p.methodId, amount: p.amount })),
    });

    try {
      // Try Cloud Print first (if enabled)
      if (shouldUseCloudPrint(cloudPrintSettingOn, order)) {
        await withPrintAnimation(preview, () => cloudPrintJob({
          printType: 'Payment',
          // Field names must match the backend EscPosReceiptData DTO exactly
          // (JSON binding is case-insensitive but does not translate names).
          // DateTime is intentionally omitted: the backend stamps it in the
          // restaurant's timezone. See CloudPrintJobService.EnqueueAsync.
          receiptData: {
            orderNumber: order!.orderNumber,
            restaurantName: restaurantSettings?.name || 'MASSRESTAURANT',
            address: restaurantSettings?.address,
            phone: restaurantSettings?.phone,
            title: 'TICKET DE PAGO',
            tableName: tableNumber,
            waiterName: order!.assignedWaiterName || '',
            items: order!.orderItems.filter(i => !i.isCancelled).map(item => ({
              name: item.productName,
              quantity: item.quantity,
              unitPrice: item.unitPrice,
              total: item.total,
              variants: item.variants?.map(v => v.variantName) || [],
            })),
            subtotal,
            tax,
            taxRate,
            discounts: appliedDiscounts.map(d => ({ reason: d.reason || 'Descuento', amount: d.discountAmount })),
            tip: tipToPrint,
            total: totalToPrint,
            payments: printPayments.map(p => ({ method: p.methodId, amount: p.amount, reference: p.reference || null })),
            autofacturaUrl,
          },
        }));
        message.success('Pago procesado e impreso exitosamente');
      }
      // Try Print Agent (Windows)
      else if (printAgentAvailable) {
        const result = await withPrintAnimation(preview, () => printAgentService.printPayment(
          order!,
          restaurantSettings?.name || 'MASSRESTAURANT',
          restaurantSettings?.address,
          restaurantSettings?.phone,
          tableNumber,
          order!.assignedWaiterName || '',
          subtotal,
          tax,
          taxRate,
          appliedDiscounts,
          tipToPrint,
          totalToPrint,
          printPayments,
          autofacturaUrl
        ));

        if (result.success) {
          message.success('Pago procesado e impreso exitosamente');
        } else {
          message.warning('Pago procesado pero falló la impresión');
          console.error('Print Agent error:', result.error);
        }
      }
      // Try Web Bluetooth if Print Agent not available (Android/Chrome)
      else if (bluetoothAvailable) {
        const result = await withPrintAnimation(preview, () => bluetoothPrintService.printPayment(
          order!,
          restaurantSettings?.name || 'MASSRESTAURANT',
          restaurantSettings?.address,
          restaurantSettings?.phone,
          tableNumber,
          order!.assignedWaiterName || '',
          subtotal,
          tax,
          taxRate,
          appliedDiscounts,
          tipToPrint,
          totalToPrint,
          printPayments,
          autofacturaUrl
        ));

        if (result.success) {
          message.success('Pago procesado e impreso exitosamente');
        } else {
          message.warning('Pago procesado pero falló la impresión');
          console.error('Bluetooth error:', result.error);
        }
      }
      // No print service available
      else {
        console.warn('No print service available');
        message.success('Pago procesado exitosamente');
      }
    } catch (printError: any) {
      // Log print error but continue anyway
      console.warn('Print failed (continuing anyway):', printError);
      message.success('Pago procesado (impresión no disponible)');
    }
  }

  // El webhook del proveedor ya registró el pago (incluida la propina de la
  // terminal) y cerró la cuenta; aquí solo se imprime y se delega el post-pago
  // al padre. La referencia usa el mismo prefijo que el backend al registrar
  // el Payment (MP-/GETNET-) para que el ticket y el arqueo cuadren.
  async function handleTerminalSettled(intent: TerminalPaymentIntent) {
    if (terminalSettling) return;
    setTerminalSettling(true);
    try {
      const settledPayments: PaymentEntry[] = [{
        id: intent.id,
        methodId: intent.paymentMethodCode || 'CARD',
        amount: intent.paidAmount > 0 ? intent.paidAmount : totalAfterDiscount + intent.tipAmount,
        reference: intent.provider === 'getnet'
          ? `GETNET-${intent.providerOrderId}`
          : `MP-${intent.providerOrderId}`,
      }];

      if (printTicket) {
        await printPaymentTicketSafe(settledPayments, intent.tipAmount);
      } else {
        message.success('Pago en terminal procesado exitosamente');
      }

      await onTerminalPaymentSettled?.();
      onCancel();
    } catch (error) {
      console.error('Terminal post-payment error:', error);
      message.warning('El pago quedó registrado, pero falló el cierre en pantalla. Refresca para ver el estado.');
      onCancel();
    } finally {
      setTerminalSettling(false);
    }
  }

  const buildReceiptData = () => {
    if (!order) return null;

    return {
      receiptId: `REC-${order.id}`,
      receiptType: 'ORDER',
      generatedAt: new Date().toISOString(),
      // Los datos del restaurante salen de su configuración, no de un objeto de relleno.
      // Antes iban hardcodeados —dirección "Calle 123 #45-67", teléfono (601) 234-5678,
      // www.massrestaurant.com y un RFC en formato colombiano— y se imprimían tal cual en
      // el ticket que se le entrega al comensal. Un dato faltante se deja vacío: el ticket
      // sin dirección es un ticket incompleto, pero el ticket con la dirección de otro es
      // un ticket falso.
      restaurant: {
        name: restaurantSettings?.name || '',
        address: {
          street: restaurantSettings?.address || '',
          city: '',
          state: '',
          postalCode: '',
          country: '',
        },
        phone: restaurantSettings?.phone || '',
        email: restaurantSettings?.email || '',
        website: '',
        taxId: '',
        legalName: '',
      },
      order: {
        orderId: order.id,
        tableNumber: tableNumber,
        areaName: displayAreaName,
        orderDate: order.openTime || new Date().toISOString(),
        orderCloseDate: new Date().toISOString(),
        serverName: order.assignedWaiterName || '',
        cashierName: '',
        guestCount: (order.adults || 0) + (order.children || 0),
        serviceDuration: null,
      },
      customer: null,
      items: activeItems.map(item => ({
        name: item.productName,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        total: item.total,
        variants: item.variants?.map(v => v.variantName) || [],
        notes: item.notes || '',
        category: '',
        isCancelled: false,
        cancellationReason: null,
      })),
      payments: payments.map(payment => {
        const method = apiPaymentMethods?.find(pm => pm.code === payment.methodId);
        return {
          method: payment.methodId,
          methodDisplayName: method?.name || payment.methodId,
          amount: payment.amount,
          reference: payment.reference || '',
          processedAt: new Date().toISOString(),
          change: changeAmount > 0 ? changeAmount : 0,
        };
      }),
      discounts: appliedDiscounts.map(discount => ({
        type: 'amount',
        value: discount.discountAmount,
        amount: discount.discountAmount,
        reason: discount.reason,
        authorizedBy: '',
      })),
      totals: {
        subtotal,
        taxAmount: tax,
        taxRate: 0.16,
        discountAmount: totalDiscountAmount,
        tipAmount: finalTip,
        grandTotal,
        amountPaid: totalPaid,
        change: changeAmount,
        roundingAdjustment: 0,
      },
      settings: {
        language: 'es',
        currency: 'MXN',
        currencySymbol: '$',
        dateFormat: 'dd/MM/yyyy HH:mm',
        showLogo: true,
        showQrCode: false,
        showTaxDetails: true,
        showServerInfo: true,
        showItemCategories: false,
        printSize: '80mm',
        template: 'default',
      },
      notes: '',
      footer: '¡Gracias por su preferencia!',
      qrCodeData: null,
    };
  };


  const receiptData = buildReceiptData();

  return (
    <>
    <Modal
      title={null}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={isMobile ? '100vw' : 1200}
      style={{
        // Sin `top` fuera del teléfono: `centered` ya lo posiciona, y un top en
        // línea le gana a la regla de AntD que lo pone en 0.
        top: isMobile ? 0 : undefined,
        // NO fijar 1200px aquí. AntD trae `max-width: calc(100vw - 32px)` en su
        // hoja de estilos justamente para que el modal quepa siempre, pero esto
        // es un estilo EN LÍNEA y se la gana. Con `centered`, AntD centra con un
        // puntal inline-block de alto completo: un modal de 1200px no cabe al
        // lado del puntal en una tablet, salta al renglón siguiente y aparece
        // una pantalla entera más abajo. Reportado por un cliente en tablet.
        maxWidth: isMobile ? '100vw' : 'calc(100vw - 32px)',
        height: isMobile ? '100vh' : 'auto'
      }}
      styles={{ 
        body: { 
          padding: 0, 
          height: isMobile ? '100vh' : 'calc(100vh - 100px)',
          overflowY: isMobile ? 'auto' : 'hidden'
        } 
      }}
      destroyOnClose
      centered={!isMobile}
    >
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ 
          padding: isMobile ? '12px 16px' : '16px 24px',
          borderBottom: '1px solid var(--border-color)',
          backgroundColor: 'var(--bg-secondary)'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: isMobile ? 'flex-start' : 'center',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? '8px' : '0'
          }}>
            <Space size={isMobile ? "small" : "middle"}>
              <CreditCardOutlined style={{
                fontSize: isMobile ? '16px' : '20px',
                color: '#52c41a'
              }} />
              <Title level={isMobile ? 4 : 3} style={{ margin: 0 }}>
                {isMobile ? displayLabel : `Liquidar Cuenta - ${displayLabel}`}
              </Title>
              {!isMobile && displayAreaName && <Text type="secondary">({displayAreaName})</Text>}
            </Space>
            
            <Space direction={isMobile ? "horizontal" : "horizontal"} wrap>
              <Text strong style={{ 
                fontSize: isMobile ? '14px' : '16px', 
                color: '#52c41a' 
              }}>
                Total: ${grandTotal.toFixed(2)}
              </Text>
              {!isPaymentComplete && remainingAmount > 0 && (
                <Tag color="orange" style={{ fontSize: isMobile ? '10px' : '12px' }}>
                  Falta: ${remainingAmount.toFixed(2)}
                </Tag>
              )}
              {isPaymentComplete && (
                <Tag color="green" style={{ fontSize: isMobile ? '10px' : '12px' }}>
                  Pago Completo
                </Tag>
              )}
            </Space>
          </div>
          
          {/* Order Summary */}
          <div style={{
            marginTop: isMobile ? '6px' : '8px',
            padding: isMobile ? '6px 12px' : '8px 16px',
            backgroundColor: 'var(--bg-primary)',
            borderRadius: '6px',
            border: '1px solid var(--border-color)'
          }}>
            <Row gutter={isMobile ? [8, 8] : [24, 0]}>
              <Col xs={12} sm={finalTip > 0 ? 4 : 6}>
                <Text type="secondary" style={{ fontSize: isMobile ? '10px' : '14px' }}>Productos:</Text>
                <br />
                <Text strong style={{ fontSize: isMobile ? '11px' : '14px' }}>
                  {activeItems.length} items - ${subtotal.toFixed(2)}
                </Text>
              </Col>
              <Col xs={12} sm={finalTip > 0 ? 4 : 6}>
                <Text type="secondary" style={{ fontSize: isMobile ? '10px' : '14px' }}>IVA ({(taxRate * 100).toFixed(0)}%):</Text>
                <br />
                <Text strong style={{ fontSize: isMobile ? '11px' : '14px' }}>${tax.toFixed(2)}</Text>
              </Col>
              <Col xs={12} sm={finalTip > 0 ? 4 : 6}>
                <Text type="secondary" style={{ fontSize: isMobile ? '10px' : '14px' }}>Subtotal:</Text>
                <br />
                <Text strong style={{ fontSize: isMobile ? '11px' : '14px' }}>${totalBeforeDiscount.toFixed(2)}</Text>
              </Col>
              {totalDiscountAmount > 0 && (
                <Col xs={12} sm={finalTip > 0 ? 4 : 6}>
                  <Text type="secondary" style={{ fontSize: isMobile ? '10px' : '14px' }}>
                    {appliedDiscounts.length > 1 ? 'Descuentos:' : 'Descuento:'}
                  </Text>
                  <br />
                  <Text strong style={{ 
                    color: '#ff4d4f', 
                    fontSize: isMobile ? '11px' : '14px' 
                  }}>
                    -${totalDiscountAmount.toFixed(2)}
                  </Text>
                  {/* Show breakdown if multiple discounts */}
                  {appliedDiscounts.length > 1 && !isMobile && (
                    <div style={{ fontSize: '10px', color: 'var(--color-neutral-text)', marginTop: '2px' }}>
                      {appliedDiscounts.map((discount, index) => (
                        <div key={discount.id}>
                          {discount.reason}: -${discount.discountAmount.toFixed(2)}
                        </div>
                      ))}
                    </div>
                  )}
                </Col>
              )}
              {finalTip > 0 && (
                <Col xs={12} sm={4}>
                  <Text type="secondary" style={{ fontSize: isMobile ? '10px' : '14px' }}>Propina:</Text>
                  <br />
                  <Text strong style={{ 
                    color: '#722ed1', 
                    fontSize: isMobile ? '11px' : '14px' 
                  }}>
                    ${finalTip.toFixed(2)}
                  </Text>
                </Col>
              )}
              <Col xs={12} sm={finalTip > 0 ? 4 : 6}>
                <Text type="secondary" style={{ fontSize: isMobile ? '10px' : '14px' }}>Total a Pagar:</Text>
                <br />
                <Text strong style={{ 
                  color: '#52c41a', 
                  fontSize: isMobile ? '12px' : '16px' 
                }}>
                  ${grandTotal.toFixed(2)}
                </Text>
              </Col>
            </Row>
          </div>
        </div>

        {/* Content */}
        <div style={{ 
          flex: 1, 
          display: 'flex', 
          overflow: 'hidden',
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          {isMobile ? (
            // Mobile: Use Tabs for better UX
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <Tabs
                defaultActiveKey="payment"
                style={{ height: '100%' }}
                tabBarStyle={{ paddingLeft: '12px', paddingRight: '12px' }}
                items={[
                  {
                    key: 'payment',
                    label: 'Pagar',
                    children: (
                      <div style={{
                        height: 'calc(100vh - 320px)',
                        overflowY: 'auto',
                        padding: '12px 12px 40px 12px'
                      }}>
                        {/* Payment form content will go here */}
                        {renderPaymentForm()}
                      </div>
                    )
                  },
                  {
                    key: 'summary',
                    label: `Resumen (${payments.length})`,
                    children: (
                      <div style={{
                        height: 'calc(100vh - 320px)',
                        overflowY: 'auto',
                        padding: '12px 12px 40px 12px'
                      }}>
                        {/* Payment summary content will go here */}
                        {renderPaymentSummary()}
                      </div>
                    )
                  }
                ]}
              />
            </div>
          ) : (
            // Desktop: Keep two-panel layout
            <>
              {/* Left Panel - Payment Actions */}
              <div style={{ 
                width: '400px', 
                display: 'flex', 
                flexDirection: 'column',
                borderRight: '1px solid var(--border-color)',
                backgroundColor: 'var(--bg-secondary)'
              }}>
                <div style={{ padding: '16px', borderBottom: '1px solid var(--border-color)' }}>
                  <Title level={4} style={{ margin: 0 }}>
                    Métodos de Pago
                  </Title>
                </div>
                <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
                  {renderPaymentForm()}
                </div>
              </div>
              
              {/* Right Panel - Payment Summary */}
              <div style={{ 
                flex: 1, 
                display: 'flex', 
                flexDirection: 'column'
              }}>
                <div style={{ padding: '16px', borderBottom: '1px solid var(--border-color)' }}>
                  <Title level={4} style={{ margin: 0 }}>
                    Pagos Agregados
                  </Title>
                </div>
                <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
                  {renderPaymentSummary()}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Modal>

    {/* Receipt Print Preview Modal */}
    {receiptData && (
      <ReceiptPreviewModal
        visible={showPrintPreview}
        onCancel={() => setShowPrintPreview(false)}
        receiptData={receiptData}
        onPrintSuccess={() => {
          setShowPrintPreview(false);
          message.success('Recibo impreso exitosamente');
        }}
      />
    )}
    </>
  );

  // Helper function to render payment form
  function renderPaymentForm() {
    return (
      <>
        {/* Payment Method Selection */}
        <div style={{ marginBottom: isMobile ? '16px' : '20px' }}>
          <Text strong style={{
            display: 'block',
            marginBottom: '8px',
            fontSize: isMobile ? '12px' : '14px'
          }}>
            Método de Pago:
          </Text>
          <Select
            value={selectedMethodId}
            onChange={setSelectedMethodId}
            style={{ width: '100%', marginBottom: '12px' }}
            size={isMobile ? "middle" : "large"}
          >
            {paymentMethods.map(method => (
              <Option key={method.id} value={method.id}>
                <Space>
                  <span style={{ color: method.color }}>{method.icon}</span>
                  {method.name}
                </Space>
              </Option>
            ))}
          </Select>

          {/* Amount Input */}
          <Text strong style={{ 
            display: 'block', 
            marginBottom: '8px',
            fontSize: isMobile ? '12px' : '14px'
          }}>
            Monto:
          </Text>
          <InputNumber
            value={currentAmount}
            onChange={(value) => setCurrentAmount(value || 0)}
            onFocus={(e) => e.target.select()}
            style={{ width: '100%', marginBottom: '12px' }}
            prefix="$"
            precision={2}
            min={0}
            size={isMobile ? "middle" : "large"}
          />

          {/* Quick amount button */}
          <Row gutter={8} style={{ marginBottom: '12px' }}>
            <Col span={24}>
              <Button
                size={isMobile ? "small" : "middle"}
                block
                onClick={() => handleQuickAmount(remainingAmount)}
                disabled={remainingAmount <= 0}
              >
                Monto Exacto
              </Button>
            </Col>
          </Row>

          {/* Reference Input */}
          {/* En tarjeta, el folio de la terminal: con él la factura global del mes se cuadra
              contra el reporte de la terminal sin capturar nada. Opcional salvo que el método
              tenga "Requiere referencia". */}
          {(currentMethod?.requiresReference || METODOS_CON_REFERENCIA.includes(selectedMethodId)) && (
            <div style={{ marginBottom: '12px' }}>
              <Text strong style={{ 
                display: 'block', 
                marginBottom: '8px',
                fontSize: isMobile ? '12px' : '14px'
              }}>
                Referencia {currentMethod?.requiresReference ? '*' : '(Opcional)'}:
              </Text>
              <input
                type="text"
                value={currentReference}
                onChange={(e) => setCurrentReference(e.target.value)}
                placeholder={
                  ES_TARJETA.includes(selectedMethodId) ? 'Folio de la terminal (opcional)' :
                  selectedMethodId === 'transfer' ? 'Núm. de transferencia (opcional)' :
                  selectedMethodId === 'mobile' ? 'Núm. de referencia (opcional)' :
                  'Núm. de referencia o voucher'
                }
                style={{
                  width: '100%',
                  padding: isMobile ? '6px 10px' : '8px 12px',
                  border: '1px solid var(--border-color-strong)',
                  borderRadius: '6px',
                  fontSize: isMobile ? '12px' : '14px',
                  backgroundColor: 'var(--bg-primary)',
                  color: 'var(--text-primary)'
                }}
              />
            </div>
          )}

          {/* Add Payment Button */}
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddPayment}
            block
            size={isMobile ? "middle" : "large"}
            disabled={currentAmount <= 0}
            style={{ marginBottom: isMobile ? '16px' : '20px' }}
          >
            Agregar Pago
          </Button>

          {/* Terminal bancaria (MP Point o Getnet, según TerminalProvider) */}
          {terminalPayEnabled && (
            <>
              <Divider style={{ margin: '4px 0 16px 0' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>o cobra todo con tarjeta</Text>
              </Divider>
              <TerminalPointSection
                provider={terminalProvider as 'mercadopago' | 'getnet'}
                target={terminalTarget!}
                onOrderAlreadySplit={onOrderAlreadySplit}
                amount={totalAfterDiscount}
                // En ambos proveedores la propina se captura en la terminal, no
                // en el POS: una propina local se bloquea.
                tipAmount={0}
                disabledReason={
                  payments.length > 0
                    ? 'Quita los pagos agregados: el cobro en terminal es por el total de la cuenta'
                    : finalTip > 0
                      ? 'Quita la propina: en el cobro con terminal la propina se captura en el dispositivo'
                      : undefined
                }
                onSettled={handleTerminalSettled}
                isMobile={isMobile}
              />
            </>
          )}
        </div>

        <Divider />

        {/* Tip Section */}
        <div style={{ marginBottom: isMobile ? '16px' : '20px' }}>
          <Text strong style={{ 
            display: 'block', 
            marginBottom: '12px',
            fontSize: isMobile ? '12px' : '14px'
          }}>
            Propina (Opcional):
          </Text>
          <Row gutter={8} style={{ marginBottom: '12px' }}>
            {tipSuggestions.map((tip, index) => (
              <Col span={8} key={index}>
                <Button
                  size="small"
                  block
                  type={suggestedTip === tip.value ? 'primary' : 'default'}
                  onClick={() => handleSuggestedTip(tip.value)}
                  style={{ fontSize: isMobile ? '10px' : '12px' }}
                >
                  {tip.label}
                </Button>
              </Col>
            ))}
          </Row>
          <InputNumber
            placeholder="Propina personalizada"
            value={customTip}
            onChange={(value) => {
              setCustomTip(value || 0);
              setSuggestedTip(0);
            }}
            onFocus={(e) => e.target.select()}
            style={{ width: '100%' }}
            prefix="$"
            precision={2}
            min={0}
            size={isMobile ? "middle" : "large"}
          />
        </div>

      </>
    );
  }

  // Helper function to render payment summary
  function renderPaymentSummary() {
    return (
      <>
        {payments.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: isMobile ? '20px' : '40px',
            color: 'var(--color-neutral-text)'
          }}>
            <CreditCardOutlined style={{ 
              fontSize: isMobile ? '32px' : '48px', 
              marginBottom: '16px' 
            }} />
            <div style={{ fontSize: isMobile ? '12px' : '14px' }}>
              No hay pagos agregados
            </div>
            <div style={{ 
              fontSize: isMobile ? '10px' : '12px', 
              marginTop: '8px' 
            }}>
              {isMobile ? 'Usa la pestaña "Pagar" para agregar métodos' : 'Agrega métodos de pago desde el panel izquierdo'}
            </div>
          </div>
        ) : (
          <div>
            {payments.map((payment, index) => {
              // Find method by code since payment.methodId now contains the code
              const apiMethod = apiPaymentMethods?.find(pm => pm.code === payment.methodId);
              const method = apiMethod ? {
                id: apiMethod.id,
                name: apiMethod.name,
                icon: getPaymentMethodIcon(apiMethod.code),
                color: apiMethod.color || getPaymentMethodColor(apiMethod.code),
                requiresReference: apiMethod.requiresReference,
              } : null;
              return (
                <div key={payment.id} style={{ 
                  marginBottom: '12px',
                  padding: isMobile ? '12px' : '16px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  backgroundColor: 'var(--bg-primary)'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? '8px' : '0'
                  }}>
                    <div style={{ flex: 1, width: isMobile ? '100%' : 'auto' }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px', 
                        marginBottom: '4px',
                        justifyContent: isMobile ? 'center' : 'flex-start'
                      }}>
                        <span style={{ 
                          color: method?.color, 
                          fontSize: isMobile ? '14px' : '16px' 
                        }}>
                          {method?.icon}
                        </span>
                        <Text strong style={{ 
                          fontSize: isMobile ? '14px' : '16px' 
                        }}>
                          {method?.name}
                        </Text>
                      </div>
                      <Text style={{ 
                        fontSize: isMobile ? '16px' : '20px', 
                        color: '#52c41a', 
                        fontWeight: 'bold',
                        display: 'block',
                        textAlign: isMobile ? 'center' : 'left'
                      }}>
                        ${payment.amount.toFixed(2)}
                      </Text>
                      {payment.reference && (
                        <div style={{ 
                          fontSize: isMobile ? '10px' : '12px',
                          color: 'var(--color-neutral-text)',
                          marginTop: '4px',
                          textAlign: isMobile ? 'center' : 'left'
                        }}>
                          Referencia: {payment.reference}
                        </div>
                      )}
                    </div>
                    <Button
                      danger
                      icon={<MinusOutlined />}
                      onClick={() => handleRemovePayment(payment.id)}
                      size={isMobile ? "middle" : "large"}
                      style={{ width: isMobile ? '100%' : 'auto' }}
                    >
                      Eliminar
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Payment Summary Card */}
        {payments.length > 0 && (
          <div style={{ 
            marginTop: '20px',
            padding: isMobile ? '16px' : '20px',
            backgroundColor: 'var(--bg-success-light)',
            border: '1px solid var(--border-success)',
            borderRadius: '8px'
          }}>
            <Title level={5} style={{ 
              margin: '0 0 12px 0',
              fontSize: isMobile ? '14px' : '16px'
            }}>
              Resumen de Pagos
            </Title>
            
            <Row justify="space-between" style={{ marginBottom: '8px' }}>
              <Text style={{ fontSize: isMobile ? '12px' : '14px' }}>Cuenta:</Text>
              <Text strong style={{ fontSize: isMobile ? '12px' : '16px' }}>
                ${totalAfterDiscount.toFixed(2)}
              </Text>
            </Row>
            
            {finalTip > 0 && (
              <Row justify="space-between" style={{ marginBottom: '8px' }}>
                <Text style={{ fontSize: isMobile ? '12px' : '14px' }}>Propina:</Text>
                <Text strong style={{ 
                  fontSize: isMobile ? '12px' : '16px', 
                  color: '#722ed1' 
                }}>
                  ${finalTip.toFixed(2)}
                </Text>
              </Row>
            )}
            
            <Row justify="space-between" style={{ marginBottom: '8px' }}>
              <Text style={{ fontSize: isMobile ? '12px' : '14px' }}>Total a Pagar:</Text>
              <Text strong style={{ fontSize: isMobile ? '12px' : '16px' }}>
                ${grandTotal.toFixed(2)}
              </Text>
            </Row>
            
            <Row justify="space-between" style={{ marginBottom: '8px' }}>
              <Text style={{ fontSize: isMobile ? '12px' : '14px' }}>Total Pagado:</Text>
              <Text strong style={{ 
                color: totalPaid >= grandTotal ? '#52c41a' : '#fa8c16', 
                fontSize: isMobile ? '12px' : '16px' 
              }}>
                ${totalPaid.toFixed(2)}
              </Text>
            </Row>
            
            <Divider style={{ margin: '12px 0' }} />
            
            <Row justify="space-between">
              <Text strong style={{ 
                fontSize: isMobile ? '14px' : '18px', 
                color: remainingAmount > 0 ? '#fa8c16' : '#52c41a' 
              }}>
                {remainingAmount > 0 ? 'Falta:' : changeAmount > 0 ? 'Cambio:' : 'Completo:'}
              </Text>
              <Text strong style={{ 
                fontSize: isMobile ? '14px' : '18px', 
                color: remainingAmount > 0 ? '#fa8c16' : '#52c41a' 
              }}>
                ${Math.abs(remainingAmount).toFixed(2)}
              </Text>
            </Row>

            {/* Action Buttons */}
            <div style={{ marginTop: '20px' }}>
              {/* Print Ticket Checkbox */}
              <div style={{ marginBottom: '12px' }}>
                <Checkbox
                  checked={printTicket}
                  onChange={(e) => setPrintTicket(e.target.checked)}
                >
                  <PrinterOutlined style={{ marginRight: '4px' }} />
                  Imprimir Ticket
                </Checkbox>
              </div>

              <Row gutter={12}>
                <Col span={24}>
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    onClick={handleProcessPayment}
                    loading={loading}
                    disabled={!isPaymentComplete}
                    block
                    size={isMobile ? "middle" : "large"}
                    style={{
                      backgroundColor: '#52c41a',
                      borderColor: '#52c41a',
                      marginBottom: '8px'
                    }}
                  >
                    Procesar Pago
                  </Button>
                </Col>
              </Row>
            </div>
          </div>
        )}
      </>
    );
  }
};

export default PaymentModal;